import { LoaderFunction } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Auth
import { AuthContextType } from '../../../../../hooks/useAuth';

// GQL
import { client } from '../../../../../gql/client';
import { GET_SURVEILLANCE_ROUTE_BY_ID } from '../../../../../gql/queries/surveillanceRoute';
import { selectWorkOrdersWithMultimedia } from '../../../../../gql/selectors/multimedia';
import {
  GET_ORDER_BY_ID,
  GET_SELECTORS_DATA,
} from '../../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../../gql/queries/orders/predefinedData';

// Redux
import { store } from '../../../../../state/configureStore';
import { preferServerWithCacheFallback } from '../../../../../gql/cacheFallback';
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../../state/actions/ui/loadingData';

// Types
import {
  tableIds,
} from '../../../../../components/display/tables/types';
import { OrderType } from '../../../../../types/orders';
import { matchesPkRange } from '../../../../../utils/multimediaPkFilter';

// ═══════════════════════════════════════════════════════════════════
// V4 P7 FR-7 — Loader Multimèdia del RV (2026-05-06)
//
// Reusa el patrón de `LoaderOrderMultimediaPage` (work-orders) con un step
// extra: primero resuelve `route.orderIDs[0]` desde el SR id (params.routeId)
// y después carga el árbol de Order parent + children, sin discriminar tipo
// (igual que el canónico de work-orders). Por D0 las hijas de un RV solo
// pueden ser Tramos (RouteSegment) y Actuacions básiques; ambos pueden tener
// fotos desde 2026-08-09 — el tramo, las de la intervención leve.
// ═══════════════════════════════════════════════════════════════════

const applyCursorFilters = (orders: any[], cursor: any): any[] => {
  if (!cursor) return orders;


  return orders.filter(
    (o) =>
      matchesPkRange(o, cursor) &&
      (cursor.state == null || o.state === cursor.state) &&
      (cursor.capitolId == null || o.capitolIDs?.includes(cursor.capitolId)) &&
      (cursor.subCapitolId == null ||
        o.subCapitolIDs?.includes(cursor.subCapitolId)) &&
      (cursor.classification == null ||
        o.classificationIDs?.includes(cursor.classification)) &&
      (cursor.predefinedOrderId == null ||
        o.predefinedOrderIDs?.includes(cursor.predefinedOrderId)) &&
      (cursor.roadId == null || o.roadIDs?.includes(cursor.roadId)) &&
      (cursor.concessionId == null ||
        o.concessionIDs?.includes(cursor.concessionId)) &&
      (cursor.indexType == null || o.indexType === cursor.indexType) &&
      (cursor.indexExpedition == null ||
        o.indexExpedition === cursor.indexExpedition) &&
      (cursor.fromDate == null ||
        new Date(o.registerInitDate) >= new Date(cursor.fromDate)) &&
      (cursor.toDate == null ||
        new Date(o.registerInitDate) <= new Date(cursor.toDate)),
  );
};

export const LoaderRouteMultimedia: (
  userData: AuthContextType | null,
) => LoaderFunction = (userData) => async ({ params }) => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const cursorMultimedia = getCursor(tableIds.MULTIMEDIA)(store.getState());
  const dispatch = store.dispatch;
  const roleActive = userData?.user?.user?.rolActive;
  const routeId = params.routeId;

  // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
  // anterior interrumpido (React Router cancel, hot reload, throw mid-fetch).
  // Sin esto, si el run anterior abrió el flag y no llegó al cierre, este
  // run cae en el early-return o en el path sin MISS y nunca lo cierra →
  // tabla bloqueada permanentemente.
  dispatch(loadingData(tableIds.MULTIMEDIA, false));

  if (!routeId || !userData?.user) {
    return {
        totalCount: 0,
        dataOrderWithMultimedia: [],
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        filtersDataOptions: {
          capitols: [],
          subCapitols: [],
          roads: [],
          concessions: [],
          classifications: [],
          predefinedOrders: [],
        },
        loading: false,
        roleActive,
        parentOrderRoid: '',
        parentOrderType: undefined,
      };
  }

  dispatch(loadingData(tableIds.MULTIMEDIA, true));

  /**
   * El cuerpo entero, parametrizado por DE DÓNDE salen los datos.
   *
   * 🔴 Las fotos de las hijas se piden `network-only`, una por hija. Con lie-fi
   * —la petición que ni responde ni falla— esta pestaña se quedaba en la rueda
   * con las miniaturas ya cacheadas de la visita anterior. Es la misma familia
   * que el detalle y el listado; aquí el respaldo enseña las fotos que ya se
   * habían descargado, que es exactamente lo que el operario estaba viendo.
   */
  const build = async (mode: 'network' | 'cache') => {
    const policy = mode === 'cache' ? 'cache-only' : 'cache-first';
    const orderPolicy = mode === 'cache' ? 'cache-only' : 'network-only';
    // 1) Resolve SR → orderId del Order parent (entidad doble V4)
    //
    // Fix (feedback usuario 2026-07-07): `cache-first` en vez de
    // `network-only` — este mismo documento (misma query + variables) ya
    // lo cargó la tab Vista prèvia momentos antes; refetchearlo a red en
    // cada cambio de tab solo añadía latencia sin aportar datos frescos
    // (el contenido multimedia real, que sí necesita frescura, se resuelve
    // más abajo con `GET_ORDER_BY_ID` en `network-only`, sin cambios).
    const { data: srData } = await client.query({
      query: GET_SURVEILLANCE_ROUTE_BY_ID,
      variables: { id: routeId },
      fetchPolicy: policy,
    });
    const route = srData?.surveillanceRoute;
    const parentOrderId = route?.orderIDs?.[0];

    if (!parentOrderId) {
      return {
          totalCount: 0,
          dataOrderWithMultimedia: [],
          pageInfo: { hasNextPage: false, hasPreviousPage: false },
          filtersDataOptions: {
            capitols: [],
            subCapitols: [],
            roads: [],
            concessions: [],
            classifications: [],
            predefinedOrders: [],
          },
          loading: false,
          roleActive,
          parentOrderRoid: '',
          parentOrderType: OrderType.SurveillanceRoute,
        };
    }

    // 2) Cargar Order parent + sus children directos
    const { data: orderData } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: parentOrderId },
      fetchPolicy: orderPolicy,
    });

    const parentOrder = orderData?.order;
    const rawOrders: any[] = [];

    if (parentOrder) {
      // El Order del RV padre puede tener fotos propias del RV; lo incluimos.
      rawOrders.push(parentOrder);

      if (parentOrder.childExpeditionOrderIDs?.length > 0) {
        const children = await Promise.all(
          parentOrder.childExpeditionOrderIDs.map(async (childId: string) => {
            const { data: childData } = await client.query({
              query: GET_ORDER_BY_ID,
              variables: { id: childId },
              fetchPolicy: orderPolicy,
            });
            return childData?.order;
          }),
        );
        // Antes se filtraba a `OrderType.Action`, dando por hecho que los
        // Tramos nunca tendrían fotos propias. Desde 2026-08-09 sí las tienen
        // —las de la intervención leve— y ese filtro las dejaba invisibles
        // aquí Y fuera del ZIP de descarga del recorrido.
        //
        // El canónico por-orden (`WorkOrderDetailed/MultimediaPage`) carga
        // padre + TODAS las hijas sin discriminar tipo, y el selector ya
        // descarta las que no tienen imágenes (`order.imageIDs.length > 0`).
        // Por D0, las hijas de un recorrido solo pueden ser RouteSegment y
        // Actuació básica, así que no entra nada ajeno.
        const childOrders = children.filter(Boolean);
        rawOrders.push(...childOrders);
      }
    }

    const filteredRawOrders = applyCursorFilters(rawOrders, cursorMultimedia);
    const dataOrderWithMultimedia = await selectWorkOrdersWithMultimedia(
      filteredRawOrders,
    );

    // 3) Cargar metadata para los filtros (mismo patrón que work-orders)
    const [
      { data: { capitols } },
      { data: { subCapitols } },
      { data: { roads, concessions } },
      { data: { classifications } },
      { data: { predefinedOrders } },
    ] = await Promise.all([
      client.query({ query: FETCH_CAPITOLS, fetchPolicy: policy }),
      client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: policy }),
      client.query({ query: GET_SELECTORS_DATA, fetchPolicy: policy }),
      client.query({ query: FETCH_CLASSIFICATIONS, fetchPolicy: policy }),
      client.query({ query: FETCH_PREDEFINED_ORDERS, fetchPolicy: policy }),
    ]);

    const combinedData = {
      totalCount: dataOrderWithMultimedia.length,
      dataOrderWithMultimedia,
      pageInfo: { hasNextPage: false, hasPreviousPage: false },
      filtersDataOptions: {
        capitols,
        subCapitols,
        roads,
        concessions,
        classifications,
        predefinedOrders,
      },
      loading: false,
      roleActive,
      parentOrderRoid: parentOrder?.roid,
      parentOrderType: OrderType.SurveillanceRoute,
    };

    return combinedData;
  };

  try {
    const served = await preferServerWithCacheFallback(build('network'), async () => {
      const cached = await build('cache');
      // Sin fotos cacheadas no hay nada que adelantar: se sigue esperando.
      return cached.dataOrderWithMultimedia.length > 0 ? cached : null;
    });
    dispatch(loadingData(tableIds.MULTIMEDIA, false));
    return served;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: 'surveillance-route', loader: 'RouteMultimedia' },
    });
    dispatch(loadingData(tableIds.MULTIMEDIA, false));
    return {
        totalCount: 0,
        dataOrderWithMultimedia: [],
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        filtersDataOptions: {
          capitols: [],
          subCapitols: [],
          roads: [],
          concessions: [],
          classifications: [],
          predefinedOrders: [],
        },
        loading: false,
        roleActive,
        parentOrderRoid: '',
        parentOrderType: OrderType.SurveillanceRoute,
        error: err,
      };
  }
};

  return { data: load() };
};

export default LoaderRouteMultimedia;
